import { type FC } from "react";
import {
  Checkbox,
  Typography,
  Card,
  Grid,
  Box,
  Stack,
  IconButton,
  CardActionArea,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import type { PlatformPagesListRow } from "@/partner/entities/platformPages";
import { ItemTitleLink } from "@/partner/shared/components/ItemTitleLink";
import { PagePaths } from "@/partner/shared/constants";

const StyledCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "selected",
})<{ selected?: boolean }>(({ theme, selected }) => ({
  backgroundColor: selected
    ? theme.palette.action.selected
    : theme.palette.background.paper,
  padding: theme.spacing(2, 1),
  overflow: "visible",
  position: "relative",
}));

interface PreviewThumbnailProps {
  image?: { url?: string | null } | null;
}

const PreviewThumbnail: FC<PreviewThumbnailProps> = ({ image }) => {
  const { t } = useTranslation();

  if (!image?.url) {
    return (
      <Box
        sx={{
          width: 80,
          height: 60,
          backgroundColor: "action.hover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" align="center">
          {t("platformPages.item.noCover")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="img"
      src={image.url}
      alt={t("platformPages.item.previewAlt")}
      sx={{
        width: 80,
        height: 60,
        objectFit: "cover",
        borderRadius: 1,
      }}
    />
  );
};

interface PlatformPagesListItemProps {
  page: PlatformPagesListRow;
  selected: boolean;
  onSelectOneChange: (
    id: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void;
  onClickItem: (id: string) => void;
}

export const PlatformPagesListItem: FC<PlatformPagesListItemProps> = ({
  page,
  selected,
  onSelectOneChange,
  onClickItem,
}) => {
  const { t } = useTranslation();

  const style = {
    opacity: page.publishAt ? 1 : 0.6,
  };

  const handleClick = () => {
    onClickItem(page.id);
  };

  return (
    <StyledCard selected={selected} style={style} elevation={0}>
      <Grid container spacing={2} wrap="nowrap" alignItems="flex-start">
        <Grid size="auto">
          <Checkbox
            color="default"
            checked={selected}
            onChange={(event) => onSelectOneChange(page.id, event)}
            onClick={(event) => event.stopPropagation()}
          />
        </Grid>

        <Grid size="auto">
          <CardActionArea onClick={handleClick}>
            <PreviewThumbnail image={page.image} />
          </CardActionArea>
        </Grid>

        <Grid size="grow">
          <Box
            sx={{
              ml: { xs: 1.5, md: 0 },
              mr: { xs: 1.5, md: 0 },
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box sx={{ flex: 1 }}>
                <ItemTitleLink
                  to={`${PagePaths.platformPages}?tab=edit&id=${page.id}`}
                  onOpen={handleClick}
                  underline="hover"
                  variant="h6"
                  color="textPrimary"
                  sx={{
                    fontSize: { xs: "0.9rem", sm: "1.1rem", md: "1.2rem" },
                  }}
                >
                  {page.title}
                </ItemTitleLink>

                <Box sx={{ mt: 0.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    {page.path}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Box>
        </Grid>

        <Grid size="auto" container justifyContent="flex-end">
          {page.publishAt ? (
            <Box>
              <Typography variant="body2" color="textPrimary" align="right">
                {t("platformPages.item.publishedAt", { value: page.publishAt })}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="right">
                {t("platformPages.item.publishedDate", {
                  value: page.publishAt,
                })}
              </Typography>
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="textPrimary"
              align="right"
              sx={{ pr: 2 }}
            >
              {t("platformPages.item.notPublished")}
            </Typography>
          )}
        </Grid>

        <Grid size="auto">
          <Stack direction="row" alignItems="center" spacing={1}>
            {page.url && (
              <IconButton
                target="_blank"
                href={page.url}
                size="large"
                component="a"
              >
                <OpenInNewIcon />
              </IconButton>
            )}
          </Stack>
        </Grid>
      </Grid>
    </StyledCard>
  );
};
